import React, { useEffect, useState } from 'react'
import { types } from '../types.js'
import { getLink, getLinkFromRef } from '../../utility'
import { BrManageContentButton } from '@bloomreach/react-sdk'

import './textblock.css'
import { encodeForURL } from '../../../global/formatters/string-formatter'
import '../../../components/EmailSubscriptionCenter/Modal'

export function TextBlock({ page, component }) {
  const STYLES = {
    SINGLE_COLUMN: 'SingleColumn',
    TWO_COLUMN: 'TwoColumn',
  }

  const ALIGNMENT = {
    LEFT: 'Left',
    CENTER: 'Center',
  }

  const BGCOLOR = {
    WHITE: 'White',
    GRAY_LIGHT: 'Gray Light',
    BRAND: 'Brand',
    BRAND_LIGHT: 'Brand Light',
    IMAGE: 'Image',
    REVERSED_IMAGE: 'Reversed Image',
  }

  const BGCOLOR_MAPPING = {
    White: 'u-bgColorWhite',
    'Gray Light': 'u-bgColorLight',
    Brand: 'u-bgColorBrandDark',
    'Brand Light': 'u-bgColorBrandLight',
    Image: '',
    'Reversed Image': '',
  }

  const [data, setData] = useState({
    backgroundImage: null,
    iconImage: null,
    preHeader: null,
    capsHeadline: null,
    headline: null,
    introBodyContent: null,
    h3Headline: null,
    subHeadline: null,
    bodyContent: null,
    linkList: null,
  })

  const [parameter, setParameter] = useState({
    style: STYLES.SINGLE_COLUMN,
    alignment: ALIGNMENT.LEFT,
    backgroundColor: BGCOLOR.WHITE,
  })

  const _documentRef = page.getContent(component?.getModels()?.document)

  useEffect(() => {
    const _models = component?.getModels() || {}
    const { document: _documentRef } = _models
    const _document = _documentRef && page.getContent(_documentRef)
    const _data = _document?.getData() || {}

    const subscriptionTypeDocument = page.getContent(
      _document?.getData()?.topicSubscription?.$ref?.split('/')[2]
    )?.model?.data

    const translatedData = {
      backgroundImage:
        _data.backgroundImage &&
        page?.getContent(_data.backgroundImage)?.getOriginal()?.getUrl(),
      iconImage:
        _data.iconImage &&
        page?.getContent(_data.iconImage)?.getOriginal()?.getUrl(),
      preHeader: _data.preHeader,
      capsHeadline: _data.capsHeadline,
      headline: _data.headline,
      introBodyContent:
        _data.introBodyContent && typeof _data.introBodyContent === 'object'
          ? _data.introBodyContent?.value
          : _data.introBodyContent || _data.content1,
      h3Headline: _data.h3Headline,
      subHeadline: _data.subHeadline,
      bodyContent:
        _data.bodyContent && typeof _data.bodyContent === 'object'
          ? _data.bodyContent?.value
          : _data.bodyContent || _data.content2,
      linkList: _data?.linkList?.links
        .filter((l) => l.link && l.link.length > 0)
        .map((l) => getLink(getLinkFromRef(l, page), null)),
      subscriptionCenter: {
        active:
          ''.concat(subscriptionTypeDocument?.status || '').toLowerCase() ===
          'active',
        name: subscriptionTypeDocument?.displayName,
      },
    }

    setData(translatedData)

    const _parameters = component?.getParameters()

    setParameter({
      alignment: _parameters.alignment || ALIGNMENT.LEFT,
      backgroundColor: _parameters.backgroundColor || BGCOLOR.WHITE,
      style: _parameters.style || STYLES.SINGLE_COLUMN,
    })
  }, [page, component])

  const renderLinks = (linkList) => {
    return (
      <>
        {linkList?.length > 0 &&
          linkList.map((link, index) => (
            <React.Fragment key={index}>
              {link?.text && link?.url && (
                <div className="u-marginTop6gu">
                  <a
                    data-analytics={
                      link?.asButton ? 'text-block-button' : 'text-block-link'
                    }
                    className={`${
                      link?.asButton
                        ? 'Button Button--secondary'
                        : 'Link u-posRelative hover-underline-animation'
                    } ${
                      changeColor().bgDark
                        ? changeColor().font + ' bg-dark'
                        : ''
                    } ${changeColor().border}`}
                    href={link?.url}
                    target={link?.target}
                    rel="noreferrer"
                  >
                    {link?.text}
                    {!link?.asButton && (
                      <span className={`arrow Icon--material`}>
                        navigate_next
                      </span>
                    )}
                  </a>
                </div>
              )}
            </React.Fragment>
          ))}

        {data.subscriptionCenter?.active && (
          <div
            className={`u-flex u-flexCol u-flexAlignItemsStart ${
              [
                BGCOLOR.BRAND,
                BGCOLOR.BRAND_LIGHT,
                BGCOLOR.REVERSED_IMAGE,
              ].includes(parameter.backgroundColor)
                ? ''
                : 'no-reversed'
            }`}
          >
            <email-subscription-modal
              data-use-unlabeled-button="true"
              data-subscription-name={data.subscriptionCenter.name}
              tertiary
            ></email-subscription-modal>
          </div>
        )}
      </>
    )
  }

  const changeColor = () => {
    return parameter.backgroundColor !== BGCOLOR.WHITE &&
      parameter.backgroundColor !== BGCOLOR.GRAY_LIGHT &&
      parameter.backgroundColor !== BGCOLOR.IMAGE
      ? {
          font: 'u-textColorWhite',
          border: 'u-borderColorTertiary',
          bgDark: true,
        }
      : {
          font: 'u-textColorDefault',
          border: '',
          bgDark: false,
        }
  }

  const {
    backgroundImage = '',
    iconImage = '',
    preHeader = '',
    capsHeadline = '',
    headline = '',
    introBodyContent = '',
    h3Headline = '',
    subHeadline = '',
    bodyContent = '',
    linkList = [],
  } = data || {}

  return (
    <div
      data-analytics="text-block"
      className={`TextBlock u-paddingVert12gu u-posRelative ${
        BGCOLOR_MAPPING[parameter.backgroundColor]
      } ${page.isPreview() ? 'has-edit-button' : ''}`}
      style={
        (parameter.backgroundColor === BGCOLOR.IMAGE ||
          parameter.backgroundColor === BGCOLOR.REVERSED_IMAGE) &&
        backgroundImage
          ? {
              backgroundImage: 'url("' + encodeForURL(backgroundImage) + '")',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
            }
          : {}
      }
    >
      <BrManageContentButton content={_documentRef} />
      <div
        className={`u-sizeConstrained ${
          parameter.style === STYLES.SINGLE_COLUMN &&
          parameter.alignment === ALIGNMENT.CENTER
            ? 'u-flex u-flexCol u-flexAlignItemsCenter u-textCenter'
            : ''
        }`}
      >
        {iconImage && <img src={iconImage} className="u-marginBottom6gu" />}

        <div className="u-flex u-flexWrap u-flexRow">
          <div
            className={`${
              parameter.style === STYLES.TWO_COLUMN
                ? 'u-size1of2 u-sm-sizeFull u-paddingRight8gu'
                : 'u-sizeFull'
            }`}
          >
            {preHeader && (
              <h5 className={`u-marginVert1gu ${changeColor().font}`}>
                <span
                  className={`u-borderTop u-borderColorSecondary u-borderWidthMedium ${
                    changeColor().border
                  }`}
                >
                  {preHeader}
                </span>
              </h5>
            )}
            {capsHeadline && (
              <h2
                data-analytics="text-block-title"
                className={`u-marginVert1gu ${changeColor().font}`}
              >
                {capsHeadline}
              </h2>
            )}
            {headline && (
              <h2
                className={`u-marginVert1gu u-textNoTransform ${
                  changeColor().font
                }`}
              >
                {headline}
              </h2>
            )}
            {introBodyContent && (
              <div
                className={`Content ${changeColor().font} ${
                  parameter.style === STYLES.SINGLE_COLUMN &&
                  parameter.alignment === ALIGNMENT.CENTER
                    ? 'u-textCenter'
                    : ''
                }`}
                dangerouslySetInnerHTML={{
                  __html: page.rewriteLinks(introBodyContent),
                }}
              />
            )}
            {((linkList?.length || data.subscriptionCenter?.active) &&
              parameter.style === STYLES.TWO_COLUMN && (
                <div className="u-sm-hidden bottom-links-btns">
                  {renderLinks(linkList)}
                </div>
              )) ||
              ''}
          </div>

          <div
            className={`${
              parameter.style === STYLES.TWO_COLUMN
                ? `u-size1of2 u-sm-sizeFull ${
                    capsHeadline || headline || introBodyContent
                      ? 'u-sm-marginTop6gu'
                      : ''
                  }`
                : `u-sizeFull ${
                    capsHeadline || headline || introBodyContent
                      ? 'u-marginTop6gu'
                      : ''
                  }`
            }`}
          >
            {h3Headline && (
              <h3
                className={`${
                  changeColor().bgDark ? changeColor().font : 'u-textColorBrand'
                }`}
              >
                {h3Headline}
              </h3>
            )}

            {subHeadline && (
              <p
                className={`u-marginVert1gu u-textSizeMsrm1 ${
                  changeColor().font
                }`}
              >
                {subHeadline}
              </p>
            )}
            {bodyContent && (
              <div
                className={`Content ${changeColor().font} ${
                  parameter.style === STYLES.SINGLE_COLUMN &&
                  parameter.alignment === ALIGNMENT.CENTER
                    ? 'u-textCenter'
                    : ''
                }`}
                dangerouslySetInnerHTML={{
                  __html: page.rewriteLinks(bodyContent),
                }}
              />
            )}
          </div>
        </div>

        {((linkList?.length || data.subscriptionCenter?.active) && (
          <div
            className={`bottom-links-btns ${
              parameter.style === STYLES.TWO_COLUMN ? 'u-hidden u-sm-block' : ''
            }`}
          >
            {renderLinks(linkList)}
          </div>
        )) ||
          ''}
      </div>
    </div>
  )
}
TextBlock.propTypes = {
  ...types,
}
